import { useEffect, useState } from "react"
import { Link, usePage } from "@inertiajs/react"
import { ShoppingBag, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ScreenHeader } from "@/components/ui/headers"
import { QuantityStepper } from "@/components/ui/quantity-stepper"
import { createPickupCheckout } from "@/services/user-pickup-checkouts"
import { formatMoney, parseDecimal } from "./money"
import {
  cartFromServer,
  cartPayload,
  cartPayloadSignature,
  dinerServiceFee,
  lineTotal,
  usePickupCart,
  type PickupCartLine,
} from "./pickup-cart"
import { ClosedNotice } from "./closed-notice"
import {
  CouponField,
  couponRefusal,
  couponRefusalMessage,
  type AppliedCoupon,
} from "./coupon-field"
import type { PickupContext } from "./types"
import { t } from "@/lib/i18n"

/**
 * The cart the server has never seen.
 *
 * Paying is the only thing here that touches the network, and the refusal path
 * is the interesting one: the server re-reads every price from the menu, and
 * when something moved it sends back the corrected cart. This screen replaces
 * localStorage with it and tells the diner what changed, so the second tap pays
 * for a cart both sides agree on.
 */
export function PickupCartScreen() {
  const pickup = usePage<{ pickup?: PickupContext | null }>().props.pickup
  const cart = usePickupCart()

  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // Minted per attempt. The server derives its own key from the cart when this
  // is absent, but sending one lets a retried tap resolve to the same attempt
  // instead of opening a second bill.
  const [attemptId, setAttemptId] = useState(generateIdempotencyKey)
  // The typed code and the server's quote of it, kept apart on purpose: the
  // quote is priced against a specific cart and the string is not.
  const [couponCode, setCouponCode] = useState("")
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null)

  // A quote priced against three tacos is not a quote for six.
  //
  // The charge is never wrong -- `User::PickupCheckoutService` re-resolves the
  // code under the coupon's row lock and computes the reduction itself -- but a
  // stale figure on screen is the disagreement between what a diner read and
  // what their card said, which is the one thing nobody can explain afterwards.
  // So the quote is dropped and the string is kept: re-applying is one tap.
  //
  // Re-quoting automatically would be nicer and is wrong: the endpoint is an
  // oracle over a short guessable string and is limited to ten calls in five
  // minutes, so nudging the quantity stepper eleven times would lock the diner
  // out of their own coupon.
  const cartSignature = cartPayloadSignature(cart?.lines ?? [])
  useEffect(() => {
    setCoupon(null)
  }, [cartSignature])

  if (!pickup || !cart) return null

  const currency = cart.lines[0]?.currency ?? "MXN"
  const couponDiscount = coupon ? Math.min(parseDecimal(coupon.discountAmount), cart.total) : 0
  const subtotal = cart.total - couponDiscount
  // Shown before the pay button, never after. A fee a diner discovers on the
  // receipt is a fee they did not agree to, whatever the number is.
  //
  // Charged on the DISCOUNTED subtotal, because that is what
  // `User::PickupCheckoutService` charges it on: it subtracts the coupon before
  // assigning `subtotal`, and `diner_fee_amount` reads that one variable.
  const serviceFee = dinerServiceFee(subtotal, pickup.dinerFeePercentage)

  const pay = async () => {
    if (submitting || cart.lines.length === 0) return

    setSubmitting(true)
    setNotice(null)

    try {
      const result = await createPickupCheckout({
        slug: pickup.slug,
        cart: cartPayload(cart.lines),
        idempotencyKey: attemptId,
        // The code, never the amount. The server resolves what it is worth
        // against the cart it just re-read, under the coupon's lock.
        couponCode: coupon?.code ?? null,
      })

      if (result.ok) {
        window.location.assign(result.checkoutUrl)
        return
      }

      // The corrected cart first, then the explanation. Replacing it before the
      // diner reads the message is deliberate: the numbers on screen and the
      // message about them have to agree.
      if (result.cart.length > 0) cart.replaceWith(cartFromServer(result.cart))
      if (result.reason === "empty" && result.cart.length === 0) cart.clear()

      // The write path is the authority, and it just said no to this code. The
      // quote that said yes was taken seconds earlier against a coupon somebody
      // else may have spent in between, so the applied state comes off — leaving
      // it would show a discount on a total the diner can no longer be charged.
      if (couponRefusal(result.reason)) setCoupon(null)

      setNotice(refusalMessage(result.reason, result.changes))
      // A refused submission is a different one from here on: the cart it
      // described no longer exists.
      setAttemptId(generateIdempotencyKey())
    } catch {
      setNotice(t("ui.react.features.user.pickup_cart.network_error"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col text-foreground" data-testid="pickup-cart-view">
      <ScreenHeader
        title={t("ui.react.features.user.pickup_cart.title")}
        subtitle={pickup.locationName}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-4">
        {!pickup.openNow && (
          <ClosedNotice opensAt={pickup.opensAt} timeZone={pickup.timeZone} className="mt-3" />
        )}

        {cart.lines.length === 0 ? (
          <EmptyCart slug={pickup.slug} />
        ) : (
          <ul className="divide-y divide-border" data-testid="pickup-cart-lines">
            {cart.lines.map((line) => (
              <CartLineRow
                key={line.key}
                line={line}
                onQuantityChange={(quantity) => cart.setQuantity(line.key, quantity)}
                onRemove={() => cart.remove(line.key)}
              />
            ))}
          </ul>
        )}
      </div>

      {cart.lines.length > 0 && (
        <div className="shrink-0 space-y-2 border-t bg-background px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-3">
          {notice && (
            <p
              data-testid="pickup-cart-notice"
              className="rounded-lg bg-muted px-3 py-2 text-xs font-medium text-foreground"
            >
              {notice}
            </p>
          )}

          {/* Above the totals, because a code applied after reading the total
              is a code the diner has to re-read the total for. Only for a diner
              who can actually pay: a code applied and then walled behind
              registration is a promise made and taken back. */}
          {pickup.canCheckout && (
            <CouponField
              slug={pickup.slug}
              cart={cartPayload(cart.lines)}
              currency={currency}
              code={couponCode}
              onCodeChange={setCouponCode}
              applied={coupon}
              onApply={setCoupon}
              onClear={() => setCoupon(null)}
            />
          )}

          {/* The fee is broken out only when there is one. Most restaurants
              charge nothing, and a "Service fee $0.00" row on every cart is
              noise that trains people to stop reading the line that matters
              on the carts where it is not zero. A coupon breaks the total out
              for the same reason in reverse: the diner has to see the money
              come off, or the code they typed did nothing they can point at. */}
          {(serviceFee > 0 || couponDiscount > 0) && (
            <>
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-muted-foreground">
                  {t("ui.react.features.user.pickup_cart.subtotal")}
                </span>
                <span className="tabular-nums" data-testid="pickup-cart-subtotal">
                  {formatMoney(cart.total, { currency })}
                </span>
              </div>

              {couponDiscount > 0 && coupon && (
                <div className="flex items-baseline justify-between gap-4 text-sm">
                  <span className="min-w-0 truncate text-muted-foreground">
                    {coupon.promotionName || coupon.code}
                  </span>
                  <span
                    className="shrink-0 tabular-nums"
                    data-testid="pickup-cart-coupon-discount"
                  >
                    −{formatMoney(couponDiscount, { currency })}
                  </span>
                </div>
              )}

              {serviceFee > 0 && (
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t("ui.react.features.user.pickup_cart.service_fee", {
                      percentage: pickup.dinerFeePercentage,
                    })}
                  </span>
                  <span className="tabular-nums" data-testid="pickup-cart-service-fee">
                    {formatMoney(serviceFee, { currency })}
                  </span>
                </div>
              )}
            </>
          )}

          <div className="flex items-baseline justify-between text-sm">
            <span className="text-muted-foreground">
              {t("ui.react.features.user.pickup_cart.total")}
            </span>
            <span className="text-base font-semibold tabular-nums" data-testid="pickup-cart-total">
              {formatMoney(subtotal + serviceFee, { currency })}
            </span>
          </div>

          {/* Closed outranks "register to pay". Sending someone off to make an
              account for an order the kitchen will refuse either way spends
              their effort on nothing; the lines are kept, and the notice says
              when to come back. `User::PickupCheckoutService` refuses too, so a
              tab open since lunch cannot pay past this. */}
          {!pickup.openNow ? (
            <Button
              type="button"
              size="lg"
              className="h-11 w-full md:h-9"
              data-testid="pickup-cart-pay-button"
              disabled
            >
              {t("ui.react.features.user.pickup_closed.cannot_order")}
            </Button>
          ) : pickup.canCheckout ? (
            <Button
              type="button"
              size="lg"
              className="h-11 w-full md:h-9"
              data-testid="pickup-cart-pay-button"
              disabled={submitting}
              onClick={pay}
            >
              {submitting
                ? t("ui.react.features.user.pickup_cart.opening_payment")
                : t("ui.react.features.user.pickup_cart.pay")}
            </Button>
          ) : (
            // The cart survives registration, so this is an invitation rather
            // than a wall: they keep what they built and come back to it.
            <Button asChild size="lg" className="h-11 w-full md:h-9">
              <Link href="/registration/new" data-testid="pickup-cart-register-button">
                {t("ui.react.features.user.pickup_cart.register_to_pay")}
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function CartLineRow({
  line,
  onQuantityChange,
  onRemove,
}: {
  line: PickupCartLine
  onQuantityChange: (quantity: number) => void
  onRemove: () => void
}) {
  return (
    <li className="flex items-start gap-3 py-3" data-testid={`pickup-cart-line-${line.itemId}`}>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-medium">{line.name}</p>

        {line.extras.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {line.extras.map((extra) => extra.name).join(" · ")}
          </p>
        )}

        {line.notes && <p className="text-xs italic text-muted-foreground">{line.notes}</p>}

        <div className="flex items-center gap-2 pt-1">
          <QuantityStepper
            value={line.quantity}
            onChange={onQuantityChange}
            decrementTestId={`pickup-cart-decrement-${line.itemId}`}
            incrementTestId={`pickup-cart-increment-${line.itemId}`}
            valueTestId={`pickup-cart-quantity-${line.itemId}`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onRemove}
            data-testid={`pickup-cart-remove-${line.itemId}`}
            aria-label={t("ui.react.features.user.pickup_cart.remove")}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <span className="shrink-0 pt-0.5 text-sm font-semibold tabular-nums">
        {formatMoney(lineTotal(line), { currency: line.currency })}
      </span>
    </li>
  )
}

function EmptyCart({ slug }: { slug: string }) {
  return (
    <div
      className="flex flex-col items-center gap-4 py-16 text-center"
      data-testid="pickup-cart-empty"
    >
      <ShoppingBag className="size-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        {t("ui.react.features.user.pickup_cart.empty")}
      </p>
      <Button asChild variant="secondary" className="h-11 md:h-9">
        <Link href={`/r/${slug}`}>{t("ui.react.features.user.pickup_cart.back_to_menu")}</Link>
      </Button>
    </div>
  )
}

/**
 * Each refusal names what the diner has to look at. `changed` is the one that
 * carries detail — the rest are about the restaurant or the cart as a whole, and
 * listing dishes there would point at the wrong thing.
 */
function refusalMessage(reason: string, changes: { kind: string; name: string | null }[]) {
  // Every `coupon_*` refusal keeps its own sentence. The whole point of the
  // coupon flow is that "ya venció" and "ya se usó" are different facts, and a
  // `default:` branch swallowing both into "algo salió mal" would undo it here,
  // at the last screen before the payment.
  if (couponRefusal(reason)) return couponRefusalMessage(reason)

  switch (reason) {
    case "changed":
      return changedMessage(changes)
    case "empty":
      return t("ui.react.features.user.pickup_cart.refusal_empty")
    case "location_unavailable":
      return t("ui.react.features.user.pickup_cart.refusal_location_unavailable")
    // Reachable even with the pay button disabled: a tab open since lunch, or a
    // restaurant that closed between this screen loading and the tap landing.
    case "location_closed":
      return t("ui.react.features.user.pickup_cart.refusal_location_closed")
    case "registration_required":
      return t("ui.react.features.user.pickup_cart.refusal_registration_required")
    case "rate_limited":
      return t("ui.react.features.user.pickup_cart.refusal_rate_limited")
    case "stripe_not_ready":
    case "payment_unavailable":
      return t("ui.react.features.user.pickup_cart.refusal_payment_unavailable")
    default:
      return t("ui.react.features.user.pickup_cart.network_error")
  }
}

function changedMessage(changes: { kind: string; name: string | null }[]) {
  const names = changes.map((change) => change.name).filter(Boolean)

  if (names.length === 0) {
    return t("ui.react.features.user.pickup_cart.refusal_changed_generic")
  }

  return t("ui.react.features.user.pickup_cart.refusal_changed", { items: names.join(", ") })
}

function generateIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID()

  return `pickup-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
